import { useRef, useEffect, useCallback } from 'react';
import { useAssetPreviewStore } from '../store/assetPreviewStore';

interface UseAudioPlayerProps {
  instanceId: string;
  audioUrl: string | null;
}

interface UseAudioPlayerReturn {
  // State
  isPlaying: boolean;
  isPaused: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  isLooping: boolean;
  playbackRate: number;
  isReady: boolean;

  // Actions
  play: () => void;
  pause: () => void;
  stop: () => void;
  togglePlay: () => void;
  seek: (time: number) => void;
  seekPercent: (percent: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  toggleLoop: () => void;
  setPlaybackRate: (rate: number) => void;
}

export function useAudioPlayer({
  instanceId,
  audioUrl,
}: UseAudioPlayerProps): UseAudioPlayerReturn {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const {
    audioStates,
    initAudioState,
    setAudioPlaying,
    setAudioPaused,
    setAudioCurrentTime,
    setAudioDuration,
    setAudioVolume,
    setAudioMuted,
    setAudioLooping,
    setAudioPlaybackRate,
    stopAudio,
  } = useAssetPreviewStore();

  const audioState = audioStates[instanceId];

  // Initialize audio state if not exists
  useEffect(() => {
    if (!audioState) {
      initAudioState(instanceId);
    }
  }, [instanceId, audioState, initAudioState]);

  // Create and manage audio element
  useEffect(() => {
    if (!audioUrl) return;

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    // Event handlers
    const handleLoadedMetadata = () => {
      setAudioDuration(instanceId, audio.duration);
    };

    const handleEnded = () => {
      if (!audio.loop) {
        stopAudio(instanceId);
      }
    };

    const handlePlay = () => {
      setAudioPlaying(instanceId, true);
    };

    const handlePause = () => {
      if (audio.currentTime < audio.duration) {
        setAudioPaused(instanceId, true);
      }
    };

    const handleError = () => {
      console.error('Audio playback error');
      stopAudio(instanceId);
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    // Load the audio
    audio.load();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      audio.pause();
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audioRef.current = null;
    };
  }, [audioUrl, instanceId, setAudioDuration, setAudioPlaying, setAudioPaused, stopAudio]);

  // Sync audio element with store state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioState) return;

    audio.volume = audioState.volume;
    audio.muted = audioState.isMuted;
    audio.loop = audioState.isLooping;
    audio.playbackRate = audioState.playbackRate;
  }, [audioState?.volume, audioState?.isMuted, audioState?.isLooping, audioState?.playbackRate]);

  // Update current time using requestAnimationFrame for smooth updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioState?.isPlaying) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const updateTime = () => {
      if (audio && audioState?.isPlaying) {
        setAudioCurrentTime(instanceId, audio.currentTime);
        animationFrameRef.current = requestAnimationFrame(updateTime);
      }
    };

    animationFrameRef.current = requestAnimationFrame(updateTime);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioState?.isPlaying, instanceId, setAudioCurrentTime]);

  // Actions
  const play = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.play().catch(console.error);
    }
  }, []);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    stopAudio(instanceId);
  }, [instanceId, stopAudio]);

  const togglePlay = useCallback(() => {
    if (audioState?.isPlaying) {
      pause();
    } else {
      play();
    }
  }, [audioState?.isPlaying, play, pause]);

  const seek = useCallback(
    (time: number) => {
      const audio = audioRef.current;
      if (audio) {
        const clampedTime = Math.max(0, Math.min(time, audio.duration || 0));
        audio.currentTime = clampedTime;
        setAudioCurrentTime(instanceId, clampedTime);
      }
    },
    [instanceId, setAudioCurrentTime]
  );

  const seekPercent = useCallback(
    (percent: number) => {
      const audio = audioRef.current;
      if (audio && audio.duration) {
        const time = (percent / 100) * audio.duration;
        seek(time);
      }
    },
    [seek]
  );

  const setVolume = useCallback(
    (volume: number) => {
      const clampedVolume = Math.max(0, Math.min(1, volume));
      setAudioVolume(instanceId, clampedVolume);
    },
    [instanceId, setAudioVolume]
  );

  const toggleMute = useCallback(() => {
    setAudioMuted(instanceId, !audioState?.isMuted);
  }, [instanceId, audioState?.isMuted, setAudioMuted]);

  const toggleLoop = useCallback(() => {
    setAudioLooping(instanceId, !audioState?.isLooping);
  }, [instanceId, audioState?.isLooping, setAudioLooping]);

  const setPlaybackRate = useCallback(
    (rate: number) => {
      setAudioPlaybackRate(instanceId, rate);
    },
    [instanceId, setAudioPlaybackRate]
  );

  return {
    // State
    isPlaying: audioState?.isPlaying ?? false,
    isPaused: audioState?.isPaused ?? false,
    currentTime: audioState?.currentTime ?? 0,
    duration: audioState?.duration ?? 0,
    volume: audioState?.volume ?? 1,
    isMuted: audioState?.isMuted ?? false,
    isLooping: audioState?.isLooping ?? false,
    playbackRate: audioState?.playbackRate ?? 1,
    isReady: !!audioRef.current && (audioState?.duration ?? 0) > 0,

    // Actions
    play,
    pause,
    stop,
    togglePlay,
    seek,
    seekPercent,
    setVolume,
    toggleMute,
    toggleLoop,
    setPlaybackRate,
  };
}

export default useAudioPlayer;
